import React, { useCallback, useEffect, useMemo, useState } from "react";
import Tabs, { TabsProps } from "@amzn/awsui-components-react/polaris/tabs";

import {
  addOrUpdateUrlParameter,
  getValueFromUrlParameter,
  TAB_ID_KEY,
} from "../../../util/urlUtils";
import {
  DEFAULT_VALIDATION_TAB_ID,
  resolveValidationTabId,
  ValidationTabId,
} from "../../appShell/shellRoutes";
import { trackButtonClick } from "../../telemetry/telemetry";
import { DashboardTabContent } from "../DashboardTabContent";
import { CatalogIssuesTabContent } from "../catalogIssues/CatalogIssuesTabContent";
import { ProgramTicketsTabContent } from "../programTickets/ProgramTicketsTabContent";
import { StepBreakdownPivot } from "../stepBreakdown/StepBreakdownPivot";
import { PlaceholderPanel } from "./PlaceholderPanel";

export interface ValidationTabsProps {
  /** Test seam; production reads from URL. */
  readonly initialTabId?: ValidationTabId;
}

const TAB_LABELS: Readonly<Record<ValidationTabId, string>> = {
  dashboard: "Dashboard",
  "step-breakdown": "Step Breakdown",
  "program-tickets": "Program Tickets",
  "catalog-issues": "Catalog Issues",
};

const TAB_SELECT_EVENT_NAMES: Readonly<Record<ValidationTabId, string>> = {
  dashboard: "SelectDashboard",
  "step-breakdown": "SelectStepBreakdown",
  "program-tickets": "SelectProgramTickets",
  "catalog-issues": "SelectCatalogIssues",
};

const PLACEHOLDER_FEATURE_NAMES: Readonly<
  Record<
    Exclude<
      ValidationTabId,
      "dashboard" | "step-breakdown" | "program-tickets" | "catalog-issues"
    >,
    string
  >
> = {};

const TAB_ORDER: readonly ValidationTabId[] = [
  "dashboard",
  "step-breakdown",
  "program-tickets",
  "catalog-issues",
];

function readTabIdFromUrl(): ValidationTabId {
  return resolveValidationTabId(
    getValueFromUrlParameter(TAB_ID_KEY, DEFAULT_VALIDATION_TAB_ID)
  );
}

function renderTabContent(id: ValidationTabId): React.ReactNode {
  if (id === "dashboard") {
    return <DashboardTabContent />;
  }
  if (id === "step-breakdown") {
    return <StepBreakdownPivot />;
  }
  if (id === "program-tickets") {
    return <ProgramTicketsTabContent />;
  }
  if (id === "catalog-issues") {
    return <CatalogIssuesTabContent />;
  }
  return <PlaceholderPanel featureName={PLACEHOLDER_FEATURE_NAMES[id]} />;
}

export const ValidationTabs: React.FC<ValidationTabsProps> = ({
  initialTabId,
}) => {
  const [activeTabId, setActiveTabId] = useState<ValidationTabId>(
    () => initialTabId ?? readTabIdFromUrl()
  );

  useEffect(() => {
    // Synthetic "click" recorded once at mount so downstream telemetry can
    // distinguish landings from URL/bookmark ("RestoreFromUrl") from user
    // tab clicks ("SelectX") on the same feature name. `activeTabId` here
    // holds the initial value resolved by useState's lazy initializer.
    trackButtonClick("validationTabs", "RestoreFromUrl", { to: activeTabId });
  }, []);

  const handleChange = useCallback(
    (event: { detail: TabsProps.ChangeDetail }) => {
      const nextTabId = resolveValidationTabId(event.detail.activeTabId);
      if (nextTabId !== activeTabId) {
        trackButtonClick("validationTabs", TAB_SELECT_EVENT_NAMES[nextTabId], {
          from: activeTabId,
          to: nextTabId,
        });
      }
      addOrUpdateUrlParameter(TAB_ID_KEY, nextTabId);
      setActiveTabId(nextTabId);
    },
    [activeTabId]
  );

  // Use lazy content rendering so each tab's content stays mounted
  // after first activation. Switching tabs then preserves local UI
  // state (expanded rows, toggles, table pagination) and skips
  // recomputing memoized derivations from cached Redux data.
  const tabs = useMemo<TabsProps.Tab[]>(
    () =>
      TAB_ORDER.map((id) => ({
        id,
        label: TAB_LABELS[id],
        contentRenderStrategy: "lazy",
        content: renderTabContent(id),
      })),
    []
  );

  return (
    <Tabs
      data-testid="validation-tabs"
      activeTabId={activeTabId}
      onChange={handleChange}
      tabs={tabs}
    />
  );
};

export default ValidationTabs;
